import React, {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  createContext as createReactContext,
  useContext,
  useState,
} from 'react';

// Define a generic that extends an object for the context value
type ContextProps<T> = [T, Dispatch<SetStateAction<T>>];

/**
 * Creates a context, provider and a hook to access the context.
 * @param defaultValue - The default value for the context.
 * @returns An array containing the Provider and a hook to access the context.
 */
export function createContext<T>(contextName: string, defaultValue: T) {
  const Context = createReactContext<ContextProps<T> | undefined>(undefined);

  const Provider: FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
    const [state, setState] = useState<T>(defaultValue);

    return <Context.Provider value={[state, setState] as const}>{children}</Context.Provider>;
  };

  const useContextHook = () => {
    const context = useContext(Context);
    if (!context) {
      throw new Error(`use${contextName}Context must be used within a ${contextName}Provider`);
    }
    return context;
  };

  return [Provider, useContextHook] as const;
}
